<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import AssignmentOverviewAPI from 'dashboard/api/assignmentOverview';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import AgentListDetails from './AgentListDetails.vue';
import {
  agentIdsForRules,
  flattenConditions,
  rulesForInbox,
  rulesForTeamInbox,
} from './helpers';

const { t } = useI18n();
const { updateAccount } = useAccount();

const isLoading = ref(true);
const isSavingMode = ref(false);
const hasError = ref(false);
const confirmDialog = ref(null);
const confirmationDescription = ref('');
const overview = ref({
  assignment_routing_mode: 'legacy',
  agents: [],
  inboxes: [],
  teams: [],
  automation_rules: [],
});

const agentsById = computed(() =>
  Object.fromEntries(overview.value.agents.map(agent => [agent.id, agent]))
);

const inboxesById = computed(() =>
  Object.fromEntries(overview.value.inboxes.map(inbox => [inbox.id, inbox]))
);

const teamsById = computed(() =>
  Object.fromEntries(overview.value.teams.map(team => [team.id, team]))
);

const assignmentPolicies = computed(() =>
  overview.value.automation_rules.filter(rule =>
    rule.actions.some(action => action.action_name === 'auto_assign')
  )
);

const activePolicyCount = computed(
  () => assignmentPolicies.value.filter(rule => rule.active).length
);

const policiesEnabled = computed(
  () => overview.value.assignment_routing_mode === 'policies'
);

const loadOverview = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await AssignmentOverviewAPI.get();
    overview.value = data.payload;
  } catch {
    hasError.value = true;
    useAlert(t('ASSIGNMENT_ROUTING.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const linkedRulesForInbox = inbox =>
  rulesForInbox(assignmentPolicies.value, inbox.id);

const linkedRulesForTeamInbox = (team, inbox) =>
  rulesForTeamInbox(assignmentPolicies.value, team.id, inbox.id);

const linkedAgentIdsForInbox = inbox =>
  agentIdsForRules(
    linkedRulesForInbox(inbox),
    inbox,
    overview.value.teams,
    overview.value.agents
  );

const linkedAgentIdsForTeamInbox = (team, inbox) =>
  agentIdsForRules(
    linkedRulesForTeamInbox(team, inbox),
    inbox,
    overview.value.teams,
    overview.value.agents
  );

const setRoutingMode = async mode => {
  const confirmationKey = mode === 'policies' ? 'POLICIES' : 'LEGACY';
  confirmationDescription.value = t(
    `ASSIGNMENT_ROUTING.MODE.CONFIRM_${confirmationKey}_DESCRIPTION`
  );
  const confirmed = await confirmDialog.value.showConfirmation();
  if (!confirmed) return;

  isSavingMode.value = true;
  try {
    await updateAccount({ assignment_routing_mode: mode }, { silent: true });
    await loadOverview();
    useAlert(t('ASSIGNMENT_ROUTING.MODE.SAVED'));
  } catch {
    useAlert(t('ASSIGNMENT_ROUTING.MODE.ERROR'));
  } finally {
    isSavingMode.value = false;
  }
};

const conditionCount = rule => flattenConditions(rule.conditions || []).length;

const referencedInboxNames = rule => {
  const inboxIds = flattenConditions(rule.conditions || [])
    .filter(condition => condition.attribute_key === 'inbox_id')
    .flatMap(condition =>
      Array.isArray(condition.values) ? condition.values : []
    )
    .map(Number);

  return [...new Set(inboxIds)]
    .map(inboxId => inboxesById.value[inboxId]?.name)
    .filter(Boolean);
};

const eventLabel = eventName => {
  const key = `AUTOMATION.EVENTS.${eventName.toUpperCase()}`;
  const translated = t(key);
  return translated === key ? eventName.replaceAll('_', ' ') : translated;
};

const firstActionParam = action => {
  const params = Array.isArray(action.action_params)
    ? action.action_params
    : [action.action_params];
  return params[0];
};

const isAgentRemoval = action =>
  action.action_name === 'assign_agent' && firstActionParam(action) === 'nil';

const isTeamRemoval = action =>
  action.action_name === 'remove_assigned_team' ||
  (action.action_name === 'assign_team' &&
    ['nil', '0', 0, null, undefined].includes(firstActionParam(action)));

const autoAssignConfig = action => firstActionParam(action) || {};

const autoAssignPoolLabel = action => {
  const config = autoAssignConfig(action);

  if (config.pool === 'team') {
    return (
      teamsById.value[Number(config.team_id)]?.name ||
      t('ASSIGNMENT_ROUTING.AUTOMATION.UNKNOWN_TEAM', { id: config.team_id })
    );
  }

  if (config.pool === 'agents') {
    return t('ASSIGNMENT_ROUTING.AUTOMATION.SELECTED_AGENT_COUNT', {
      count: config.agent_ids?.length || 0,
    });
  }

  const poolKeys = {
    inbox: 'CURRENT_INBOX',
    current_team: 'CURRENT_TEAM',
  };
  return t(
    `ASSIGNMENT_ROUTING.AUTOMATION.POOLS.${poolKeys[config.pool] || 'UNKNOWN'}`
  );
};

const actionTitle = action => {
  if (isAgentRemoval(action)) {
    return t('ASSIGNMENT_ROUTING.AUTOMATION.ACTIONS.REMOVE_AGENT');
  }

  if (isTeamRemoval(action)) {
    return t('ASSIGNMENT_ROUTING.AUTOMATION.ACTIONS.REMOVE_TEAM');
  }

  const titleKeys = {
    auto_assign: 'AUTO_ASSIGN',
    assign_agent: 'ASSIGN_AGENT',
    assign_team: 'ASSIGN_TEAM',
    change_status: 'OPEN_CONVERSATION',
  };
  return t(
    `ASSIGNMENT_ROUTING.AUTOMATION.ACTIONS.${titleKeys[action.action_name]}`
  );
};

const actionTarget = action => {
  if (isAgentRemoval(action) || isTeamRemoval(action)) {
    return '';
  }

  const targetId = firstActionParam(action);

  if (action.action_name === 'auto_assign') {
    return autoAssignPoolLabel(action);
  }

  if (action.action_name === 'assign_agent') {
    return (
      agentsById.value[Number(targetId)]?.name ||
      t('ASSIGNMENT_ROUTING.AUTOMATION.UNKNOWN_AGENT', { id: targetId })
    );
  }

  if (action.action_name === 'assign_team') {
    return (
      teamsById.value[Number(targetId)]?.name ||
      t('ASSIGNMENT_ROUTING.AUTOMATION.UNKNOWN_TEAM', { id: targetId })
    );
  }

  return '';
};

const actionExplanation = action => {
  if (action.action_name !== 'auto_assign') return '';

  const config = autoAssignConfig(action);
  return t('ASSIGNMENT_ROUTING.AUTOMATION.ACTIONS.AUTO_ASSIGN_NOTE', {
    availability: t(
      `ASSIGNMENT_ROUTING.AUTOMATION.AVAILABILITY.${config.availability || 'online_only'}`
    ),
    behavior: t(
      `ASSIGNMENT_ROUTING.AUTOMATION.BEHAVIOR.${config.assignment_behavior || 'if_unassigned'}`
    ),
  });
};

onMounted(loadOverview);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('ASSIGNMENT_ROUTING.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('ASSIGNMENT_ROUTING.HEADER')"
        :description="$t('ASSIGNMENT_ROUTING.DESCRIPTION')"
        icon-name="arrow-swap"
      >
        <template #actions>
          <Button
            slate
            faded
            icon="i-lucide-refresh-cw"
            :label="$t('ASSIGNMENT_ROUTING.REFRESH')"
            :is-loading="isLoading"
            @click="loadOverview"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div
        v-if="hasError"
        class="rounded-lg border border-n-ruby-5 bg-n-ruby-2 p-6"
      >
        <p class="mb-4 text-sm text-n-ruby-11">
          {{ $t('ASSIGNMENT_ROUTING.ERROR') }}
        </p>
        <Button
          ruby
          faded
          :label="$t('ASSIGNMENT_ROUTING.RETRY')"
          @click="loadOverview"
        />
      </div>

      <div v-else class="flex flex-col gap-10">
        <section
          class="order-4 rounded-xl border border-n-weak bg-n-alpha-1 p-5"
        >
          <div
            class="flex flex-col justify-between gap-4 lg:flex-row lg:items-center"
          >
            <div class="max-w-3xl">
              <div class="flex items-center gap-2">
                <h2 class="text-base font-semibold text-n-slate-12">
                  {{ $t('ASSIGNMENT_ROUTING.MODE.TITLE') }}
                </h2>
                <span
                  class="rounded-md px-2 py-1 text-xs font-medium"
                  :class="
                    policiesEnabled
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-amber-3 text-n-amber-11'
                  "
                >
                  {{
                    policiesEnabled
                      ? $t('ASSIGNMENT_ROUTING.MODE.POLICIES_ACTIVE')
                      : $t('ASSIGNMENT_ROUTING.MODE.LEGACY_ACTIVE')
                  }}
                </span>
              </div>
              <p class="mt-2 text-sm text-n-slate-11">
                {{
                  policiesEnabled
                    ? $t('ASSIGNMENT_ROUTING.MODE.POLICIES_DESCRIPTION')
                    : $t('ASSIGNMENT_ROUTING.MODE.LEGACY_DESCRIPTION')
                }}
              </p>
              <p class="mt-2 text-xs text-n-slate-10">
                {{
                  $t('ASSIGNMENT_ROUTING.MODE.POLICY_COUNT', {
                    count: activePolicyCount,
                  })
                }}
              </p>
            </div>
            <div class="flex shrink-0 flex-wrap gap-2">
              <Button
                slate
                faded
                :disabled="!policiesEnabled"
                :is-loading="isSavingMode"
                :label="$t('ASSIGNMENT_ROUTING.MODE.USE_LEGACY')"
                @click="setRoutingMode('legacy')"
              />
              <Button
                teal
                :disabled="policiesEnabled || !activePolicyCount"
                :is-loading="isSavingMode"
                :label="$t('ASSIGNMENT_ROUTING.MODE.USE_POLICIES')"
                @click="setRoutingMode('policies')"
              />
            </div>
          </div>
          <p
            v-if="!policiesEnabled && !activePolicyCount"
            class="mt-3 text-xs text-n-ruby-11"
          >
            {{ $t('ASSIGNMENT_ROUTING.MODE.NEEDS_POLICY') }}
          </p>
          <p v-else-if="!policiesEnabled" class="mt-3 text-xs text-n-amber-11">
            {{ $t('ASSIGNMENT_ROUTING.MODE.COVERAGE_WARNING') }}
          </p>
        </section>

        <section class="order-2 flex flex-col gap-4">
          <div class="flex items-start gap-3">
            <div
              class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-n-blue-3 text-n-blue-11"
            >
              <Icon icon="i-lucide-inbox" class="size-5" />
            </div>
            <div>
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ $t('ASSIGNMENT_ROUTING.INBOX.TITLE') }}
              </h2>
              <p class="mt-1 max-w-3xl text-sm text-n-slate-11">
                {{ $t('ASSIGNMENT_ROUTING.INBOX.DESCRIPTION') }}
              </p>
            </div>
          </div>

          <div class="overflow-x-auto rounded-xl border border-n-weak">
            <table class="min-w-full divide-y divide-n-weak text-left text-sm">
              <thead
                class="bg-n-alpha-2 text-xs font-medium uppercase text-n-slate-10"
              >
                <tr>
                  <th class="px-4 py-3">
                    {{ $t('ASSIGNMENT_ROUTING.INBOX.INBOX') }}
                  </th>
                  <th class="px-4 py-3">
                    {{ $t('ASSIGNMENT_ROUTING.COMMON.RULES') }}
                  </th>
                  <th class="px-4 py-3">
                    {{ $t('ASSIGNMENT_ROUTING.INBOX.TARGETS') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr v-for="inbox in overview.inboxes" :key="inbox.id">
                  <td class="px-4 py-4 align-top">
                    <router-link
                      class="font-medium text-n-slate-12 hover:text-n-brand"
                      :to="{
                        name: 'settings_inbox_show',
                        params: { inboxId: inbox.id },
                      }"
                    >
                      {{ inbox.name }}
                    </router-link>
                    <p class="mt-1 text-xs text-n-slate-10">
                      {{ inbox.channel_type.replace('Channel::', '') }}
                      ·
                      {{
                        $t('ASSIGNMENT_ROUTING.INBOX.MEMBER_COUNT', {
                          count: inbox.member_ids.length,
                        })
                      }}
                    </p>
                  </td>
                  <td class="px-4 py-4 align-top">
                    <div
                      v-if="linkedRulesForInbox(inbox).length"
                      class="flex max-w-md flex-wrap gap-1.5"
                    >
                      <router-link
                        v-for="rule in linkedRulesForInbox(inbox)"
                        :key="rule.id"
                        :to="{ name: 'automation_list' }"
                        class="rounded-md border border-n-weak bg-n-alpha-1 px-2 py-1 text-xs text-n-slate-11 hover:text-n-brand"
                        :class="{ 'opacity-60': !rule.active }"
                      >
                        {{ rule.name }}
                        <span v-if="!rule.active">
                          · {{ $t('ASSIGNMENT_ROUTING.COMMON.INACTIVE') }}
                        </span>
                      </router-link>
                    </div>
                    <span v-else class="text-xs text-n-slate-9">
                      {{ $t('ASSIGNMENT_ROUTING.COMMON.NO_LINKED_RULES') }}
                    </span>
                  </td>
                  <td class="px-4 py-4 align-top">
                    <AgentListDetails
                      :agent-ids="linkedAgentIdsForInbox(inbox)"
                      :agents="overview.agents"
                    />
                    <p
                      v-if="inbox.policy_capacity_excluded_agent_ids.length"
                      class="mt-2 text-xs text-n-amber-11"
                    >
                      {{
                        $t('ASSIGNMENT_ROUTING.INBOX.CAPACITY_EXCLUDED', {
                          count:
                            inbox.policy_capacity_excluded_agent_ids.length,
                        })
                      }}
                    </p>
                  </td>
                </tr>
                <tr v-if="!overview.inboxes.length">
                  <td
                    colspan="3"
                    class="px-4 py-10 text-center text-n-slate-10"
                  >
                    {{ $t('ASSIGNMENT_ROUTING.INBOX.EMPTY') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section class="order-3 flex flex-col gap-4">
          <div class="flex items-start gap-3">
            <div
              class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-n-amber-3 text-n-amber-11"
            >
              <Icon icon="i-lucide-users-round" class="size-5" />
            </div>
            <div>
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ $t('ASSIGNMENT_ROUTING.TEAM.TITLE') }}
              </h2>
              <p class="mt-1 max-w-3xl text-sm text-n-slate-11">
                {{ $t('ASSIGNMENT_ROUTING.TEAM.DESCRIPTION') }}
              </p>
            </div>
          </div>

          <div class="overflow-x-auto rounded-xl border border-n-weak">
            <table class="min-w-full divide-y divide-n-weak text-left text-sm">
              <thead
                class="bg-n-alpha-2 text-xs font-medium uppercase text-n-slate-10"
              >
                <tr>
                  <th
                    class="sticky left-0 z-10 min-w-52 bg-n-alpha-2 px-4 py-3"
                  >
                    {{ $t('ASSIGNMENT_ROUTING.TEAM.TEAM') }}
                  </th>
                  <th
                    v-for="inbox in overview.inboxes"
                    :key="inbox.id"
                    class="min-w-56 px-4 py-3"
                  >
                    {{ inbox.name }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr v-for="team in overview.teams" :key="team.id">
                  <td
                    class="sticky left-0 z-10 bg-n-background px-4 py-4 align-top"
                  >
                    <router-link
                      class="font-medium text-n-slate-12 hover:text-n-brand"
                      :to="{
                        name: 'settings_teams_edit',
                        params: { teamId: team.id },
                      }"
                    >
                      {{ team.name }}
                    </router-link>
                    <p class="mt-1 text-xs text-n-slate-10">
                      {{
                        $t('ASSIGNMENT_ROUTING.TEAM.MEMBER_COUNT', {
                          count: team.member_ids.length,
                        })
                      }}
                    </p>
                  </td>
                  <td
                    v-for="inbox in overview.inboxes"
                    :key="inbox.id"
                    class="px-4 py-4 align-top"
                  >
                    <template
                      v-if="linkedRulesForTeamInbox(team, inbox).length"
                    >
                      <div class="mb-2 flex max-w-md flex-wrap gap-1.5">
                        <router-link
                          v-for="rule in linkedRulesForTeamInbox(team, inbox)"
                          :key="rule.id"
                          :to="{ name: 'automation_list' }"
                          class="rounded-md border border-n-weak bg-n-alpha-1 px-2 py-1 text-xs text-n-slate-11 hover:text-n-brand"
                          :class="{ 'opacity-60': !rule.active }"
                        >
                          {{ rule.name }}
                          <span v-if="!rule.active">
                            · {{ $t('ASSIGNMENT_ROUTING.COMMON.INACTIVE') }}
                          </span>
                        </router-link>
                      </div>
                      <AgentListDetails
                        :agent-ids="linkedAgentIdsForTeamInbox(team, inbox)"
                        :agents="overview.agents"
                      />
                    </template>
                    <span v-else class="text-xs text-n-slate-9">
                      {{ $t('ASSIGNMENT_ROUTING.COMMON.NO_LINKED_RULES') }}
                    </span>
                  </td>
                </tr>
                <tr v-if="!overview.teams.length">
                  <td
                    :colspan="overview.inboxes.length + 1"
                    class="px-4 py-10 text-center text-n-slate-10"
                  >
                    {{ $t('ASSIGNMENT_ROUTING.TEAM.EMPTY') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section class="order-1 flex flex-col gap-4">
          <div class="flex items-start gap-3">
            <div
              class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-n-iris-3 text-n-iris-11"
            >
              <Icon icon="i-lucide-workflow" class="size-5" />
            </div>
            <div>
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ $t('ASSIGNMENT_ROUTING.AUTOMATION.POLICY_TITLE') }}
              </h2>
              <p class="mt-1 max-w-3xl text-sm text-n-slate-11">
                {{ $t('ASSIGNMENT_ROUTING.AUTOMATION.POLICY_DESCRIPTION') }}
              </p>
            </div>
          </div>

          <div
            v-if="overview.automation_rules.length"
            class="grid gap-4 lg:grid-cols-2"
          >
            <article
              v-for="rule in overview.automation_rules"
              :key="rule.id"
              class="rounded-xl border border-n-weak bg-n-alpha-1 p-4"
            >
              <div class="flex items-start justify-between gap-3">
                <div>
                  <h3 class="font-medium text-n-slate-12">{{ rule.name }}</h3>
                  <p
                    v-if="rule.description"
                    class="mt-1 text-sm text-n-slate-10"
                  >
                    {{ rule.description }}
                  </p>
                </div>
                <span
                  class="inline-flex shrink-0 rounded-md px-2 py-1 text-xs font-medium"
                  :class="
                    rule.active
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-alpha-2 text-n-slate-10'
                  "
                >
                  {{
                    rule.active
                      ? $t('ASSIGNMENT_ROUTING.COMMON.ACTIVE')
                      : $t('ASSIGNMENT_ROUTING.COMMON.INACTIVE')
                  }}
                </span>
              </div>

              <div class="mt-3 flex flex-wrap gap-2 text-xs text-n-slate-10">
                <span class="rounded-md bg-n-alpha-2 px-2 py-1">
                  {{ eventLabel(rule.event_name) }}
                </span>
                <span class="rounded-md bg-n-alpha-2 px-2 py-1">
                  {{
                    $t('ASSIGNMENT_ROUTING.AUTOMATION.CONDITION_COUNT', {
                      count: conditionCount(rule),
                    })
                  }}
                </span>
                <span
                  v-if="rule.recheck_delay_seconds"
                  class="rounded-md bg-n-alpha-2 px-2 py-1"
                >
                  {{
                    $t('ASSIGNMENT_ROUTING.AUTOMATION.DELAY', {
                      seconds: rule.recheck_delay_seconds,
                    })
                  }}
                </span>
              </div>

              <div
                v-if="referencedInboxNames(rule).length"
                class="mt-3 text-xs text-n-slate-10"
              >
                {{ $t('ASSIGNMENT_ROUTING.AUTOMATION.INBOX_REFERENCES') }}
                {{ referencedInboxNames(rule).join(', ') }}
              </div>

              <ol class="mt-4 flex flex-col gap-2">
                <li
                  v-for="action in rule.actions"
                  :key="`${rule.id}-${action.position}`"
                  class="flex gap-3 rounded-lg border border-n-weak bg-n-background p-3"
                >
                  <span
                    class="flex size-6 shrink-0 items-center justify-center rounded-full bg-n-iris-3 text-xs font-semibold text-n-iris-11"
                  >
                    {{ action.position + 1 }}
                  </span>
                  <div>
                    <p class="text-sm font-medium text-n-slate-12">
                      {{ actionTitle(action) }}
                      <span
                        v-if="actionTarget(action)"
                        class="font-normal text-n-slate-11"
                      >
                        · {{ actionTarget(action) }}
                      </span>
                    </p>
                    <p
                      v-if="actionExplanation(action)"
                      class="mt-1 text-xs text-n-slate-10"
                    >
                      {{ actionExplanation(action) }}
                    </p>
                  </div>
                </li>
              </ol>
            </article>
          </div>

          <div
            v-else
            class="rounded-xl border border-dashed border-n-weak px-4 py-10 text-center text-sm text-n-slate-10"
          >
            {{ $t('ASSIGNMENT_ROUTING.AUTOMATION.EMPTY') }}
          </div>

          <div>
            <router-link :to="{ name: 'automation_list' }">
              <Button
                slate
                faded
                icon="i-lucide-external-link"
                :label="$t('ASSIGNMENT_ROUTING.AUTOMATION.OPEN_AUTOMATION')"
              />
            </router-link>
          </div>
        </section>
      </div>
    </template>
    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('ASSIGNMENT_ROUTING.MODE.CONFIRM_TITLE')"
      :description="confirmationDescription"
    />
  </SettingsLayout>
</template>
